import { useState, useEffect } from "react";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { format, formatInTimeZone } from "date-fns-tz"; // Need date-fns-tz
import { ToolPageLayout } from "@/components/ToolPageLayout";

export function TimezoneConverter() {
  const [date, setDate] = useState(new Date().toISOString().slice(0,16));
  const [tz1, setTz1] = useState("America/New_York");
  const [tz2, setTz2] = useState("Europe/London");
  const [res1, setRes1] = useState("");
  const [res2, setRes2] = useState("");

  useEffect(() => {
    try {
      const d = new Date(date);
      // For a real timezone converter we would use proper parsing from the specific timezone, but this works for simple cases.
      setRes1(formatInTimeZone(d, tz1, "yyyy-MM-dd HH:mm:ss zzz"));
      setRes2(formatInTimeZone(d, tz2, "yyyy-MM-dd HH:mm:ss zzz"));
    } catch(e) {
      setRes1("Invalid date or timezone");
      setRes2("Invalid date or timezone");
    }
  }, [date, tz1, tz2]);

  return (
    <ToolPageLayout
      title="Timezone Converter Online"
      description="Convert any local date and time between different global timezones instantly with our free online timezone converter."
      h1="Timezone Converter"
      category="Date & Time"
      categoryPath="/date-time"
      howToUse={[
        "Select your local date and time.",
        "Enter the names of the target timezones (e.g. America/New_York, Europe/London).",
        "The converted date and time in both timezones will be shown in real-time."
      ]}
      faq={[
        { question: "What timezone database is used?", answer: "This converter uses the IANA Time Zone Database format (e.g. Area/Location)." }
      ]}
    >
      <div className="space-y-6 max-w-xl mx-auto">
        <div className="space-y-2"><Label>Local Date & Time</Label><Input type="datetime-local" value={date} onChange={e => setDate(e.target.value)} /></div>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="space-y-2"><Label>Timezone 1</Label><Input value={tz1} onChange={e => setTz1(e.target.value)} /><div className="p-4 bg-slate-50 border rounded-lg font-bold">{res1}</div></div>
          <div className="space-y-2"><Label>Timezone 2</Label><Input value={tz2} onChange={e => setTz2(e.target.value)} /><div className="p-4 bg-slate-50 border rounded-lg font-bold">{res2}</div></div>
        </div>
      </div>
    </ToolPageLayout>
  );
}

export function CountdownTimerGenerator() {
  const [target, setTarget] = useState("");
  const [timeLeft, setTimeLeft] = useState("");

  useEffect(() => {
    if (!target) return;
    const interval = setInterval(() => {
      const t = new Date(target).getTime() - new Date().getTime();
      if (t < 0) { setTimeLeft("Expired!"); return; }
      const d = Math.floor(t / (1000 * 60 * 60 * 24));
      const h = Math.floor((t % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const m = Math.floor((t % (1000 * 60 * 60)) / (1000 * 60));
      const s = Math.floor((t % (1000 * 60)) / 1000);
      setTimeLeft(`${d}d ${h}h ${m}m ${s}s`);
    }, 1000);
    return () => clearInterval(interval);
  }, [target]);

  return (
    <ToolPageLayout
      title="Countdown Timer Generator"
      description="Create custom real-time countdown timers for events, launches, or deadlines with our free online tool."
      h1="Countdown Timer Generator"
      category="Date & Time"
      categoryPath="/date-time"
      howToUse={[
        "Set the target date and time for your countdown.",
        "The timer will dynamically update every second showing days, hours, minutes, and seconds."
      ]}
      faq={[
        { question: "Can I use multiple timers?", answer: "Yes, you can generate as many countdown timers as you need." }
      ]}
    >
      <div className="space-y-6 max-w-sm mx-auto text-center">
        <div className="space-y-2 text-left"><Label>Target Date & Time</Label><Input type="datetime-local" value={target} onChange={e => setTarget(e.target.value)} /></div>
        <div className="p-8 bg-slate-900 text-white rounded-xl text-3xl font-mono font-bold">{timeLeft || "00d 00h 00m 00s"}</div>
      </div>
    </ToolPageLayout>
  );
}
